import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { BaseParamsBuilderResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import {
  FilterFieldNames, FilterOps, IStringValuedFilterClause, IValuedFieldFilterClause
} from '~src/spacesPlatform/models/IFilterClause';

import { ContentRelayPlanMapper } from '../mappers/ContentRelayPlanMapper';
import { IContentRelayPlanInfo } from '../models/IContentRelayPlanInfo';
import {
  GetContentRelayPlansRequest,
  IGetContentRelayPlansParams,
  IGetContentRelayPlansResult,
} from '../serverApi/requests/GetContentRelayPlansRequest';

/** The largest page `listPlans` returns (PlanService caps pageSize at 100). */
export const LIST_PLANS_MAX_PAGE_SIZE = 100;

/**
 * Resource query backing the Content Relay plan list. Unlike standard VizPortal getItems APIs
 * (offset-paged), `listPlans` is token-paged and reads `state` / `pageSize` / `pageToken` payload
 * params, so we build those explicitly rather than reusing the generic ResourceQuery/fetcher.
 *
 * NOTE: `getSlice(start, size)` requests the first page only — token continuation
 * (`nextPageToken`) is not yet threaded through the offset-based slice contract. This is enough
 * for the initial list view; paging beyond the first page is a follow-up. See W-23846068.
 */
export class ContentRelayPlansResourceQuery extends BaseParamsBuilderResourceQuery<IContentRelayPlanInfo> {

  public getSlice(start: number, size: number): Promise<ListResult<IContentRelayPlanInfo>> {
    const extraParams = this.getExtraParams() || {};
    const params: IGetContentRelayPlansParams = {
      state: extraParams.state,
      pageSize: size != null ? size : undefined,
      pageToken: extraParams.pageToken,
    };

    return WebHttpClient.sendRequest(new GetContentRelayPlansRequest(params))
      .then((result: IGetContentRelayPlansResult) => {
        // The monolith Web API can return an HTTP 200 with an `errors` payload (e.g. code 17
        // UnexpectedErrorOccurred) when the downstream content-mobility gRPC service is not
        // reachable from the current dataplane. That resolves the promise rather than rejecting
        // it, so we detect it here and fall through to an empty list. Degrading to empty keeps
        // the Explorer on its configured empty state instead of rendering a blank/broken view.
        if (!result || (result.errors && result.errors.length > 0) || !result.plans) {
          return ListResult.empty<IContentRelayPlanInfo>();
        }
        const allItems = ContentRelayPlanMapper.map(result.plans);

        // `listPlans` has no server-side search, owner or date filters, so apply the filter panel's keyword,
        // owner and created / modified date clauses client-side over the loaded page. NOTE: this only matches plans in
        // the fetched page — full filtering needs server-side support (+ token paging). See W-23846058 follow-up.
        const matchesFilters = this.clientSideFilter();
        if (matchesFilters) {
          const filtered = allItems.filter(matchesFilters);
          return new ListResult<IContentRelayPlanInfo>(filtered, start || 0, filtered.length, [], false);
        }
        return new ListResult<IContentRelayPlanInfo>(allItems, start || 0, result.totalCount, [], !!result.nextPageToken);
      })
      .catch(() => {
        // Network/HTTP-level failure (rejected promise, e.g. the service endpoint is missing).
        // Degrade to an empty list rather than letting the rejection propagate to the space saga,
        // which would fail the whole fetch and blank the view.
        return ListResult.empty<IContentRelayPlanInfo>();
      });
  }

  public getCount(): Promise<number> {
    return this.getSlice(0, 1).then((slice: ListResult<IContentRelayPlanInfo>) => slice.total);
  }

  public getFirst(): Promise<IContentRelayPlanInfo> {
    throw new Error('ContentRelayPlansResourceQuery getFirst not implemented');
  }

  /**
   * A predicate for the active keyword (`matches`), owner (`eq` on ownerId) and created / modified date
   * (`gte` / `lte` on createdAt / updatedAt) clauses, or null when none are set.
   */
  private clientSideFilter(): ((item: IContentRelayPlanInfo) => boolean) | null {
    const tests: Array<(item: IContentRelayPlanInfo) => boolean> = [];
    (this.getFilters() || []).forEach((clause) => {
      const fieldClause = clause as IValuedFieldFilterClause<string>;
      if (clause.operator === FilterOps.matches && !fieldClause.field) {
        const needle = ((clause as IStringValuedFilterClause).value || '').toLowerCase();
        if (needle) {
          tests.push((item) => (item.name || '').toLowerCase().indexOf(needle) !== -1);
        }
      } else if (clause.operator === FilterOps.eq && fieldClause.field === FilterFieldNames.ownerId) {
        const ownerId = String(fieldClause.value);
        tests.push((item) => item.ownerId === ownerId);
      } else if ((clause.operator === FilterOps.gte || clause.operator === FilterOps.lte)
        && (fieldClause.field === FilterFieldNames.createdAt || fieldClause.field === FilterFieldNames.updatedAt)) {
        const bound = new Date(fieldClause.value).getTime();
        if (isNaN(bound)) {
          return;
        }
        const isAfter = clause.operator === FilterOps.gte;
        const field = fieldClause.field;
        tests.push((item) => {
          const date = field === FilterFieldNames.createdAt ? item.createdAt : item.updatedAt;
          if (!date) {
            return false;
          }
          return isAfter ? date.getTime() >= bound : date.getTime() <= bound;
        });
      }
    });
    return tests.length ? (item) => tests.every((test) => test(item)) : null;
  }
}
